import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

import { api, clearCsrfToken, setCsrfToken, UnauthorizedError } from "../api/client";
import type { HeaderUser } from "../ui/Header";

const ME_KEY = ["me"] as const;

export function useMe() {
  return useQuery<HeaderUser | null>({
    queryKey: ME_KEY,
    queryFn: async () => {
      try {
        const me = await api.get<HeaderUser>("/api/me");
        // Refresh CSRF token while we're at it (idempotent).
        const csrf = await api.get<{ csrf_token: string }>("/api/auth/csrf");
        setCsrfToken(csrf.csrf_token);
        return me;
      } catch (err) {
        if (err instanceof UnauthorizedError) return null;
        throw err;
      }
    },
    staleTime: 30_000,
  });
}

export function useLogin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { email: string; password: string }) => {
      const res = await api.post<{
        user_id: string;
        email: string;
        role: string;
        mfa_required: boolean;
        csrf_token: string;
      }>("/api/auth/login", input);
      setCsrfToken(res.csrf_token);
      return res;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ME_KEY }),
  });
}

export function useLogout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      await api.post("/api/auth/logout");
      clearCsrfToken();
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ME_KEY }),
  });
}
